import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosInstance";

const fmt = (v) => '₹' + Math.round(parseFloat(v) || 0).toLocaleString('en-IN');
const num = (v) => parseInt(v) || 0;

const StatBox = ({ label, value, icon, color }) => (
    <div className="col-md-3 col-sm-6 mb-3">
        <div className="info-box">
            <span className="info-box-icon" style={{ backgroundColor: color }}>
                <i className={icon}></i>
            </span>
            <div className="info-box-content">
                <span className="info-box-text">{label}</span>
                <span className="info-box-number">{value}</span>
            </div>
        </div>
    </div>
);

export default function AccountsBusinessSummary() {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({});
    const [loans, setLoans] = useState([]);

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const [s, f] = await Promise.all([
                axiosInstance.get('/dashboard/stats'),
                axiosInstance.get('/finance/open'),
            ]);
            setStats(s.data || {});
            setLoans(Array.isArray(f.data) ? f.data : []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const portfolio    = loans.reduce((s, l) => s + (parseFloat(l.loan_amount)  || 0), 0);
    const dueBalance   = loans.reduce((s, l) => s + (parseFloat(l.due_balance)  || 0), 0);
    const fineBalance  = loans.reduce((s, l) => s + (parseFloat(l.fine_balance) || 0), 0);
    const overdueCount = loans.filter(l => (parseFloat(l.fine_balance) || 0) > 0).length;
    const efficiencyPct = portfolio > 0 ? ((portfolio - dueBalance) / portfolio * 100).toFixed(1) : 0;

    return (
        <div className="content-wrapper">
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0">
                                <i className="fas fa-tachometer-alt text-warning mr-2"></i>Business Summary
                            </h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb float-sm-right">
                                <li className="breadcrumb-item"><a href="/accounts">Accounts</a></li>
                                <li className="breadcrumb-item active">Business Summary</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <section className="content">
                <div className="container-fluid">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-warning" role="status"></div>
                        </div>
                    ) : (
                        <>
                            <h6 className="text-muted text-uppercase mb-2">Today's Overview</h6>
                            <div className="row">
                                <StatBox label="Today's Collection" value={fmt(stats.today_collection)}         icon="fas fa-coins"            color="#28a745" />
                                <StatBox label="Due Today"          value={`${num(stats.todays_due)} loans`}   icon="fas fa-calendar-day"     color="#ffc107" />
                                <StatBox label="Monthly Collection" value={fmt(stats.monthly_collection)}       icon="fas fa-calendar-alt"     color="#007bff" />
                                <StatBox label="Receipts Issued"    value={num(stats.monthly_receipts)}        icon="fas fa-receipt"          color="#17a2b8" />
                            </div>

                            <h6 className="text-muted text-uppercase mb-2 mt-2">Loan Portfolio</h6>
                            <div className="row">
                                <StatBox label="Active Loans"  value={num(stats.active_finance)} icon="fas fa-file-invoice-dollar" color="#007bff" />
                                <StatBox label="Closed Loans"  value={num(stats.closed_finance)} icon="fas fa-check-circle"         color="#28a745" />
                                <StatBox label="Overdue Loans" value={overdueCount}               icon="fas fa-exclamation-triangle" color="#dc3545" />
                                <StatBox label="Overdue Fines" value={fmt(fineBalance)}           icon="fas fa-gavel"                color="#fd7e14" />
                            </div>

                            <div className="row mt-2">
                                <div className="col-md-4 mb-3">
                                    <div className="callout callout-info">
                                        <h5>Total Portfolio Value</h5>
                                        <p className="h3 font-weight-bold text-primary mb-0">{fmt(portfolio)}</p>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="callout callout-warning">
                                        <h5>Due Balance</h5>
                                        <p className="h3 font-weight-bold text-warning mb-0">{fmt(dueBalance)}</p>
                                    </div>
                                </div>
                                <div className="col-md-4 mb-3">
                                    <div className="callout callout-danger">
                                        <h5>Fine / Penalty Outstanding</h5>
                                        <p className="h3 font-weight-bold text-danger mb-0">{fmt(fineBalance)}</p>
                                    </div>
                                </div>
                            </div>

                            {portfolio > 0 && (
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Collection Efficiency</h3>
                                        <div className="card-tools">
                                            <span className="badge badge-success">{efficiencyPct}%</span>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="progress mb-2" style={{ height: 12 }}>
                                            <div
                                                className="progress-bar bg-success"
                                                style={{ width: `${efficiencyPct}%` }}
                                            ></div>
                                        </div>
                                        <small className="text-muted">
                                            {fmt(portfolio - dueBalance)} collected of {fmt(portfolio)} total portfolio
                                        </small>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </div>
    );
}
